import { useOpenS3File } from '@/components/hooks/useOpenS3File';
import { FlexColumnDiv } from '@/components/shared/components/common.StyledComponents';
import { MaskField } from '@/components/shared/components/MaskedField';
import {
    coerceStringToDateNoTime,
    formatAddress,
    formatDate,
    formatPhoneNumber,
    formatSSN,
} from '@/components/shared/utils/utils';
import styled from '@emotion/styled';
import { ButtonVariants, rem } from '@triumph/design-system';
import { ReactNode } from 'react';
import { FileButton } from '../Applicant.StyledComponents';
import { OwnerProps } from '../Applicant.types';
import { ApplicantInfoCard, ApplicantInfoRow } from './ApplicantInfoCard';
import { Au10tixDddLink } from './Au10tixDddLink';

const FileRow = styled.div`
    display: flex;
    align-items: center;
    gap: ${rem(12)};
`;

export const OwnerInformation = ({
    legalFirstName,
    legalMiddleName,
    legalLastName,
    doB,
    ssnOrItin,
    email,
    homePhone,
    mobilePhone,
    homeAddress,
    businessRole,
    businessOwnershipPercentage,
    files,
    isControllingManager,
    driversLicenseExpiration,
    driversLicenseNumber,
    driversLicenseState,
    subTitle,
    isSubmission = false,
}: OwnerProps & { subTitle?: ReactNode; isSubmission?: boolean }) => {
    const { handleFileClick, getFileDragProps } = useOpenS3File();

    const sanitizedDoB = formatDate(coerceStringToDateNoTime(doB) ?? '');
    const sanitizedLicense = formatDate(
        (driversLicenseExpiration ? coerceStringToDateNoTime(driversLicenseExpiration) : '') ?? '',
    );
    return (
        <ApplicantInfoCard title="Owner Information" subTitle={subTitle}>
            <ApplicantInfoRow
                label="Legal First Name"
                value={legalFirstName}
                data-testid="legalFirstName"
            />
            <ApplicantInfoRow
                label="Legal Middle Name"
                value={legalMiddleName}
                data-testid="legalMiddleName"
            />
            <ApplicantInfoRow
                label="Legal Last Name"
                value={legalLastName}
                data-testid="legalLastName"
            />
            <ApplicantInfoRow label="DOB" value={sanitizedDoB} data-testid="doB" />
            <ApplicantInfoRow
                label="SSN/ITIN"
                value={ssnOrItin === null ? null : <MaskField value={formatSSN(ssnOrItin)} />}
                data-testid="ssnOrItin"
            />
            <ApplicantInfoRow label="Business Email" value={email} data-testid="email" />
            <ApplicantInfoRow
                label="Home Phone"
                value={formatPhoneNumber(homePhone)}
                data-testid="homePhone"
            />
            <ApplicantInfoRow
                label="Mobile Phone"
                value={formatPhoneNumber(mobilePhone)}
                data-testid="mobilePhone"
            />
            <ApplicantInfoRow
                label="Home Address"
                value={formatAddress({
                    line1: homeAddress?.line1 ?? '',
                    line2: homeAddress?.line2 ?? '',
                    city: homeAddress?.city ?? '',
                    state: homeAddress?.stateOrTerritory ?? '',
                    zip: homeAddress?.zip ?? '',
                })}
                key="owner-mailing-address"
                data-testid="owner-mailing-address"
            />
            <ApplicantInfoRow
                label="Company/Job Title"
                value={businessRole}
                data-testid="businessRole"
            />
            <ApplicantInfoRow
                label="Company Ownership %"
                value={businessOwnershipPercentage}
                data-testid="businessOwnershipPercentage"
            />
            <ApplicantInfoRow
                label="Do you Run the Business (Controlling Manager)?"
                value={
                    isControllingManager === null ? null : isControllingManager ? 'Yes' : 'No'
                }
                data-testid="isControllingManager"
            />
            <ApplicantInfoRow
                label="Driver's License #"
                value={
                    driversLicenseNumber === null ? null : (
                        <MaskField value={driversLicenseNumber ?? ''} />
                    )
                }
                data-testid="driversLicenseNumber"
            />
            <ApplicantInfoRow
                label="Driver's License Expiration Date"
                value={sanitizedLicense}
                data-testid="driversLicenseExpiration"
            />
            <ApplicantInfoRow
                label="Driver's License State"
                value={driversLicenseState}
                data-testid="driversLicenseState"
            />
            <ApplicantInfoRow
                label="Government Documents"
                value={
                    files.length > 0 && (
                        <FlexColumnDiv>
                            {files.map((file, index) => (
                                <FileRow key={file.id}>
                                    <FileButton
                                        onClick={() => handleFileClick(file.id)}
                                        {...getFileDragProps(file.id, file.filename)}
                                        variant={ButtonVariants.tertiary}
                                        // A plain prop, so a compare sees a same-name replacement.
                                        data-file-id={file.id}
                                        data-testid={`owner-file-${index}`}
                                    >
                                        {file.filename}
                                    </FileButton>
                                    {isSubmission ? null : <Au10tixDddLink fileId={file.id} />}
                                </FileRow>
                            ))}
                        </FlexColumnDiv>
                    )
                }
            />
        </ApplicantInfoCard>
    );
};
